// src/com/trans_plan_download/TransPlanDownload.js
import React, { useEffect, useRef, useState, } from 'react';
import { IconButton, CircularProgress, Button } from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import {ContentConainer, ContentHeader, ControlBox, ToolBox} from '../../common/TopViewControl';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import { AppObject, AppStyle } from '../../value/AppConst';
import DateRangePicker from '../../common/DateRangePicker';
import { authTableTreeRepo, fchDcTransPlanHistoryFileListRepo } from '../../value/AppModel';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import {
    LIST_FILE_CREATE_HIS_DC_TRANS_PLAN_HISTORY_FILE, REMOVE_FILE_CREATE_HIS
} from '../file_create_his/FileCreateHisGql';
import ValueMisc from '../../value/ValueMisc';
import FlexyTable from '../../common/flexytable/FlexyTable';
import { FileCreateHisDictionary } from '../file_create_his/FileCreateHisDict';
import TransPlanReportInput from '../file_of_report/TransPlanReportInput';
import FileCreateHisDownload from '../file_create_his/FileCreateHisDownload';
import { TransPlanReportColumns, TransPlanReportMeta } from '../file_of_report/TransPlanReportDef';

const ReportMeta = TransPlanReportMeta;
const ReportExcelColumns = TransPlanReportColumns;

/**
 * 운송요청 등록과 짝을 이루는 운송요청 다운로드.
 * 주어진 시간 범위의 운송요청 묶음 파일을 다운로드.
 * 파일 생성 버튼을 눌러 운송요청 묶음 파일을 만들게 하면 완료 후 목록에 나타난다.
 * @param {*} param0 
 */
export default function TransPlanDownload({
    viewServiceTags // for switching view
}) {
    // const [duration, setDuration] = useState({dateFrom:'', dateTill:''});
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const fileCreateHisListData = useReactiveVar(fchDcTransPlanHistoryFileListRepo);
    const [openInputDialog, setOpenInputDialog] = useState(false);
    const [downloaderState, setDownloaderState] = useState({open:false, item:null, instructions:null});
    const openConfirmDialog = useConfirmDialog();
    const checkAuth = useAuthorizationCheck();
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const lastReloadRef = useRef(0);
    // const openCountRef = useRef(0);
    
    // ##### Call GraphQL to get List #####
    const [getFileCreateHisList, responseList] = useLazyQuery(LIST_FILE_CREATE_HIS_DC_TRANS_PLAN_HISTORY_FILE, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
        onError: (error) => addAlert({title: "운송요청 기록파일 조회", message: [error.message]})
    });
        
    // ##### GraphQL Mutation. 'onError' for prevent from printing error at console (Uncaught exception) ###
    const [removeFileCreateHis, responseRemove] = useMutation( REMOVE_FILE_CREATE_HIS, {
        onCompleted: (data, option) => onCompleteRemove(data, option),
        onError: (error) => addAlert({title: "운송요청 기록파일 삭제", message: [error.message]})
    });

    const getFileCreateHisListNow = (dateFrom, dateTill) => {
        const param = {variables:{dateFrom:dateFrom, dateTill:dateTill}};
        getFileCreateHisList(param);
    };
    
    useEffect(() => {
        const newData = {...fileCreateHisListData};
        const duration = newData.param;
        if(!duration.dateFrom || !duration.dateTill) {
            duration.dateFrom = ValueMisc.getYmdBefore(86400*7);
            duration.dateTill = ValueMisc.getYMD();
            newData.param = duration;
            fchDcTransPlanHistoryFileListRepo(newData);
            getFileCreateHisListNow(duration.dateFrom, duration.dateTill);
        }
    }, [fileCreateHisListData]);
    
    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        if(data.fileCreateHisDcTransPlanHistoryFileList) {
            const newData = {...fileCreateHisListData};
            newData.records = data.fileCreateHisDcTransPlanHistoryFileList;
            newData.when = new Date().getTime()/1000;
            fchDcTransPlanHistoryFileListRepo(newData);
            lastReloadRef.current = new Date().getTime()/1000;
        }
    };

    const onCompleteRemove = (data, clientOption) => {
        if(data.fileCreateHisRemove.ok) {
            const duration = fileCreateHisListData.param;
            getFileCreateHisListNow(duration.dateFrom, duration.dateTill);
        }
        else addAlert({title:"운송요청 기록파일 삭제 오류", message:[data.fileCreateHisRemove.message]});
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<

    const onChangeFrom = (ymd) => {
        // setDuration({...duration, dateFrom:ymd});
        const newData = {...fileCreateHisListData};
        if(newData.param.dateFrom !== ymd) {
            newData.param.dateFrom = ymd;
            fchDcTransPlanHistoryFileListRepo(newData);
            getFileCreateHisListNow(ymd, newData.param.dateTill);
            lastReloadRef.current = 0;
        }
    };

    const onChangeTo = (ymd) => {
        // setDuration({...duration, dateTill:ymd});
        const newData = {...fileCreateHisListData};
        if(newData.param.dateTill !== ymd) {
            newData.param.dateTill = ymd;
            fchDcTransPlanHistoryFileListRepo(newData);
            getFileCreateHisListNow(newData.param.dateFrom, ymd);
            lastReloadRef.current = 0;
        }
    };

    const onClickReload = () => {
        const diff = Math.round(new Date().getTime()/1000 - lastReloadRef.current);
        if(diff < 60) {
            // Too short to reload
            enqueueSnackbar(`동일한 조건으로는 1분 내에 검색할 수 없습니다. ${60-diff}초 후에 다시 시도해 주세요.`, {variant:'info', autoHideDuration:2000});
        }
        else {
            const duration = fileCreateHisListData.param;
            getFileCreateHisListNow(duration.dateFrom, duration.dateTill);
        }
    };

    const onCompleteReportCreation = () => {
        getFileCreateHisListNow(duration.dateFrom, duration.dateTill);
        setOpenInputDialog(false);
    };

    const openDownloaderForItem = (item) => {
        const instructions = ReportMeta.instructions;
        setDownloaderState({open:true, item:item, instructions:instructions});
    }

    const onClickCreate = () => {
        setOpenInputDialog(true);
    };
    
    const confirmRemove = async (item) => {
        const confirm = await openConfirmDialog({
            title: "운송요청 파일 삭제",
            content: [
                '운송요청 파일을 삭제합니다.',
                '삭제 후 복구할 수 없습니다. 계속하시겠습니까?'
            ]
        });

        if(confirm)
            removeFileCreateHis({variables:{fileCreateHis:{fileCreateHisSeq:item.fileCreateHisSeq, serverUrl: item.serverUrl}}});
    };

    /* const onChangeSearchText = (text) => {
        setSearchText(text);
    }; */

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.DELETE: confirmRemove(row); break;
            case AppObject.command.DOWNLOAD: openDownloaderForItem(row); break;
            default: return;
        }
    };
    const canRemove = checkAuth(authTableTree.tree.pr_file_create_his_remove);
    const tools = [
        {id:AppObject.command.DOWNLOAD, icon:<SimCardDownloadIcon/>, color: AppStyle.mui.primary},
    ];
    if(canRemove) {
        tools.push(
            {id:AppObject.command.DELETE, icon:<DeleteForeverIcon/>, color: AppStyle.mui.warning},
        );
    }

    const duration = fileCreateHisListData.param;

    return(
        <ContentConainer>
            {viewServiceTags}
            <ContentHeader>
                <ControlBox>
                    <DateRangePicker
                        dateFrom={duration.dateFrom || ''}
                        dateTo={duration.dateTill || ''}
                        onChangeFrom={onChangeFrom}
                        onChangeTo={onChangeTo}
                    />
                    {/* <SearchBox
                        value={searchText}
                        onChangeText={onChangeSearchText}
                    /> */}
                </ControlBox>
                <ToolBox>
                    {
                        responseRemove.loading ||
                        responseList.loading ?
                        <CircularProgress size={20} style={{marginLeft:10}}/>
                        :
                        <IconButton
                            color={AppStyle.mui.inherit}
                            onClick={onClickReload}
                        >
                            <CachedIcon />
                        </IconButton>
                    }
                    <Button
                        varaint={AppStyle.mui.contained}
                        color={AppStyle.mui.secondary}
                        startIcon={<NoteAddIcon />}
                        onClick={onClickCreate}
                        style={{marginLeft:10}}
                    >
                        생성
                    </Button>
                </ToolBox>
            </ContentHeader>
            <FlexyTable
                name={FileCreateHisDictionary.tableProps.tableId+"forDcTransPlanHistory"}
                tableProps={FileCreateHisDictionary.tableProps}
                records={fileCreateHisListData.records}
                tools={tools}
                onClickTool={onClickToolOnRow}
            />
            <TransPlanReportInput
                open={openInputDialog}
                onClose={() => setOpenInputDialog(false)}
                onComplete={onCompleteReportCreation}
            />
            <FileCreateHisDownload
                open={downloaderState.open}
                item={downloaderState.item}
                columns={ReportExcelColumns}
                instructions={downloaderState.instructions}
                getExtraHeaders={ReportMeta.getExtraHeaders}
                onClose={() => setDownloaderState({open:false, item:null, instructions:null})}
            />
        </ContentConainer>
    );
}